import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureBorrowerBureauConsent } from '../../../../../store/company/action';
import { fetchLatestConsents } from '../../../../../store/dashboard/creditBureau/actions';
import { toast } from 'react-toastify';

export interface ConsentInfo {
    consenterName: string;
    consentedAt: string;
}

interface UseGiveConsentLogicParams {
    assesseeOrgId?: string;
}

interface GiveConsentInput {
    pan?: string;
    mobileNumber?: string;
}

interface UseGiveConsentLogicReturn {
    giveConsent: (directorId: string, input?: GiveConsentInput) => void;
    latestConsentsByDirectorId: Record<string, ConsentInfo>;
    isLoading: boolean;
    receivedDirectorIds: Set<string>;
}

function useGiveConsentLogic({ assesseeOrgId }: UseGiveConsentLogicParams): UseGiveConsentLogicReturn {
    const dispatch = useDispatch();
    const [receivedDirectorIds, setReceivedDirectorIds] = useState<Set<string>>(new Set());
    const [inFlightDirectorIds, setInFlightDirectorIds] = useState<Set<string>>(new Set());

    // Read latest consents from the canonical Redux slice — same store path used by the
    // PNO-side KeyOfficialsCreditSummary attribution badge, so both surfaces share one
    // source of truth and the fetchLatestConsents saga only runs once per orgId.
    const latestConsentsByDirectorId = useSelector((state: any) => state?.dashboard?.creditBureau?.latestConsents) as Record<
        string,
        ConsentInfo
    >;

    useEffect(() => {
        if (assesseeOrgId) {
            dispatch(fetchLatestConsents({ orgId: assesseeOrgId }));
        }
    }, [assesseeOrgId, dispatch]);

    const giveConsent = (directorId: string, input?: GiveConsentInput) => {
        if (!directorId || !assesseeOrgId) return;
        if (inFlightDirectorIds.has(directorId)) return;

        setInFlightDirectorIds((prev) => new Set([...prev, directorId]));

        dispatch(
            captureBorrowerBureauConsent({
                assesseeOrgId,
                directorId,
                pan: input?.pan,
                mobileNumber: input?.mobileNumber,
                onSuccess: () => {
                    setReceivedDirectorIds((prev) => new Set([...prev, directorId]));
                    setInFlightDirectorIds((prev) => {
                        const next = new Set(prev);
                        next.delete(directorId);
                        return next;
                    });
                    toast.success('Consent captured successfully');
                    // Re-fetch to surface "Last consent by X" line for everyone consuming the slice.
                    if (assesseeOrgId) {
                        dispatch(fetchLatestConsents({ orgId: assesseeOrgId }));
                    }
                },
                onError: () => {
                    setInFlightDirectorIds((prev) => {
                        const next = new Set(prev);
                        next.delete(directorId);
                        return next;
                    });
                },
            }),
        );
    };

    return {
        giveConsent,
        latestConsentsByDirectorId: latestConsentsByDirectorId || {},
        isLoading: inFlightDirectorIds.size > 0,
        receivedDirectorIds,
    };
}

export default useGiveConsentLogic;
